import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import type {
  InsurerOrganizationUnit,
  InsurerOrganizationUnitDiscount,
} from 'BrokerTsLib/src/dtos'
import type { InsuranceCategoryDiscount } from 'brokertslib/src/dtos/OrganizationUnit/Discounts/InsuranceCategoryDiscount'
import { getProductFullName } from 'brokertslib/src/enums'
import {
  useGetInsurerDiscounts,
  useUpdateDiscount,
} from 'brokertslib/src/hooks/useOrganizationUnit'
import { useRef, useState } from 'react'

import { DiscountEditForm } from './DiscountEditForm'

export const DiscountsEdit = ({
  insurer,
  isOpen,
  onClose,
}: {
  insurer: InsurerOrganizationUnit
  isOpen: boolean
  onClose: () => void
}) => {
  const [editedDiscounts, setEditedDiscounts] = useState<
    { dirty: boolean; discount: InsurerOrganizationUnitDiscount }[]
  >([])
  const submitDialogLeastDestructiveRef = useRef<HTMLButtonElement>(null)
  const {
    isOpen: isSubmitDialogOpen,
    onClose: closeSubmitDialog,
    onOpen: openSubmitDialog,
  } = useDisclosure()
  const { data } = useGetInsurerDiscounts(
    insurer.organizationUnitId,
    insurer.insurerId,
    insurer.insuranceCategoryType,
  )

  const discounts:
    | (InsurerOrganizationUnitDiscount | InsuranceCategoryDiscount)[]
    | undefined = data?.data && [
      ...data.data.assignedDiscounts,
      ...data.data.availableDiscounts,
    ]

  const { mutateAsync: updateDiscount, isPending: isUpdating } =
    useUpdateDiscount()

  const toast = useToast()

  const handleSave = (values: InsurerOrganizationUnitDiscount) =>
    updateDiscount({
      organizationUnitId: insurer.organizationUnitId,
      insurerId: insurer.insurerId,
      insuranceCategoryType: insurer.insuranceCategoryType,
      applyToSubordinates: false,
      ...values,
    }).catch((e) => {
      toast({
        title: 'Chyba při ukládání slevy',
        description: e.response?.data?.message ?? '',
        status: 'error',
      })
      return e
    })

  const handleClose = () => {
    if (editedDiscounts.some((ed) => ed.dirty)) {
      openSubmitDialog()
    } else {
      onClose()
    }
  }

  const saveEditedDiscounts = () =>
    Promise.all(
      editedDiscounts.filter((x) => x.dirty).map((x) => handleSave(x.discount)),
    ).then(() => {
      setEditedDiscounts([])
      closeSubmitDialog()
      onClose()
    })

  return (
    <>
      <Modal size="4xl" isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>
            <Text sx={{ my: 1 }} fontSize={'sm'} fontWeight={'normal'}>
              {getProductFullName(insurer.insuranceCategoryType)}
            </Text>
            <Text>{`Pojišťovna: ${insurer.insurer}`}</Text>
            <ModalCloseButton />
          </ModalHeader>

          <ModalBody sx={{ pt: 0, display: 'grid', gap: 4 }}>
            {discounts &&
              discounts.map((discount, index) => {
                return (
                  <Box key={discount.insuranceCategoryDiscountId}>
                    <DiscountEditForm
                      title={`Sleva: ${discount.name}`}
                      {...insurer}
                      {...discount}
                      valid={!!(discount as any).valid}
                      conflictDiscounts={discount.conflictDiscountIds
                        .map((cd) =>
                          discounts.find(
                            (d) => d.insuranceCategoryDiscountId === cd,
                          ),
                        )
                        .filter((d) => !!d)}
                      onSubmit={handleSave}
                      onChanged={(isDirty, discount) =>
                        setEditedDiscounts((prev) => [
                          ...prev.filter(
                            (e) =>
                              e.discount.insuranceCategoryDiscountId !==
                              discount.insuranceCategoryDiscountId,
                          ),
                          {
                            dirty: isDirty,
                            discount,
                          },
                        ])
                      }
                    />
                    {index < discounts.length - 1 && <Divider />}
                  </Box>
                )
              })}
          </ModalBody>
        </ModalContent>
      </Modal>
      <AlertDialog
        isOpen={isSubmitDialogOpen}
        onClose={closeSubmitDialog}
        leastDestructiveRef={submitDialogLeastDestructiveRef}
      >
        <AlertDialogOverlay />

        <AlertDialogContent>
          <AlertDialogHeader>Neuložené změny</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            Přejete si uložit změny nebo je zahodit?
          </AlertDialogBody>
          <AlertDialogFooter gap={2}>
            <Button
              colorScheme="red"
              mr={'auto'}
              onClick={() => {
                setEditedDiscounts([])
                closeSubmitDialog()
                onClose()
              }}
              isDisabled={isUpdating}
            >
              Zahodit změny
            </Button>
            <Button
              ref={submitDialogLeastDestructiveRef}
              onClick={closeSubmitDialog}
              isDisabled={isUpdating}
            >
              Zpět
            </Button>
            <Button
              colorScheme="primary"
              onClick={saveEditedDiscounts}
              isLoading={isUpdating}
            >
              Uložit změny
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
